import { CalculatorOutlined, InfoCircleOutlined, LoadingOutlined, SettingOutlined } from '@ant-design/icons'
import { Alert, Button, Checkbox, Divider, Form, Input, InputNumber, Popover, Select, Spin, Switch, Tabs, Typography, notification } from 'antd'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'
import TextArea from 'antd/lib/input/TextArea'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { Description, calculateBeslutsstod, editBeslutsstod, getMunicipalityData, loadBeslutsstodById, saveBeslutsstod, useDownloadPdf } from '../../api/beslutsstod/beslutsstod'
import { useMe } from '../../api/permissions'
import { useApi } from '../../api/useApi'
import FlagListHeader from '../../components/header'
import { getMunicipality, setMunicipality } from '../../store/beslutsstod/municipalitySlice'
import EditableCalculationSettings from './components/editableCalculationSettings'
import FormSettings from './components/editableFormSettings'
import EditableOther from './EditableOther'
import EditableTimepoints from './components/editableTimepoints'
import TimelineTooltip from './components/timelineTooltips'
import EditTimepointsTasks from './EditTimepointTasks'
import Other from './other'
import SearchBeslutsstod from './searchBeslutsstod'
import ShowResults from './showResults'
import TimepointsTasks from './timepointsTasks'
import TaskDistributionTable from './components/taskDistributionTable'
import jsonFormatter, { FormattedJsonData } from './utils/jsonFormatter'
import { isTaskComplete } from './utils/isTaskComplete'
import jsonLoader, { JSONLoadError, getDescriptionChangeErrors, withoutErrorsForChangedFields } from './utils/jsonLoader'
import { validatePersonnummer } from './utils/validatePersonnummer'
import { MunicipalityData } from './types/municipalityData'
const api = useApi()

type Props = {}

const { TabPane } = Tabs

const Beslutsstod: React.FC<Props> = (props) => {
  const { data: me } = useMe()

  const { id } = useParams<{ id?: string }>()
  const location = useLocation()
  const { mutateAsync: downloadPdf, isLoading: isPdfLoading } = useDownloadPdf()

  const [form] = Form.useForm()
  const [tab, setTab] = useState('1')
  const [editMode, setEditMode] = useState(false)
  const [editDescription, setEditDescription] = useState('')
  const [result, setResult] = useState<any>(null)
  const [loadedResult, setLoadedResult] = useState<any>(null)
  const [checkedTimepoints, setCheckedTimepoints] = useState<string[]>([])
  const [pnr, setPnr] = useState('')
  const [pid, setPid] = useState('')
  const [note, setNote] = useState('')
  const [weeksOff, setWeeksOff] = useState(0)
  // Note for loaded evaluation
  const [loadedNote, setLoadedNote] = useState('')
  const [formattedInput, setFormattedInput] = useState<FormattedJsonData | null>(null)
  const [descriptions, setDescriptions] = useState<Description[]>([])
  const [loadingErrors, setLoadingErrors] = useState<JSONLoadError[] | null>(null)
  // Samma varningar, men bara de som ännu inte rättats – de ger röd ram i tabellerna (#79)
  const [markedLoadingErrors, setMarkedLoadingErrors] = useState<JSONLoadError[] | null>(null)
  const [submittingErrors, setSubmittingErrors] = useState<SubmitError[] | null>(null)
  const [savedStatus, setSavedStatus] = useState<{ pnr: string; time: string } | null>(null)
  const [loadedStatus, setLoadedStatus] = useState<{ pnr: string; time: string } | null>(null)
  const dispatch = useDispatch()
  const municipalityData = useSelector(getMunicipality)

  const [notificationInstance, contextHolder] = notification.useNotification()

  // Efter Beräkna glider sidan ned så att resultatet börjar överst på skärmen (#79)
  const resultRef = useRef<HTMLDivElement>(null)
  const [scrollToResult, setScrollToResult] = useState(false)
  useEffect(() => {
    if (!scrollToResult) return
    resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setScrollToResult(false)
  }, [scrollToResult])
  // Snurra i Beräkna-knappen medan uträkningen pågår (#79)
  const [calculating, setCalculating] = useState(false)
  // Vid Kopiera visas en snurra i stället för formuläret tills beräkningen är inläst (#79)
  const [loadingCopy, setLoadingCopy] = useState(id !== undefined)

  const onChange = (value: CheckboxValueType[]) => {
    const sorted = (value as string[]).sort((a, b) => {
      return (municipalityData?.input.timepoints.indexOf(a) ?? 0) - (municipalityData?.input.timepoints.indexOf(b) ?? 0)
    })

    // En tillagd eller borttagen tidpunkt döljer resultatet, som när en insats ändras (#69)
    setSavedStatus(null)
    setResult(null)
    setLoadedResult(null)
    setFormattedInput(null)
    setSubmittingErrors(null)

    setCheckedTimepoints(sorted)
  }

  const timepointOptions = (timepoints: string[]) => {
    return timepoints.map((timepoint: string) => {
      return { label: timepoint, value: timepoint }
    })
  }

  interface SubmitError {
    message: ReactNode
    reason: 'frequency' | 'level' | 'maxAllocations' | 'exclusive'
  }
  const getErroneousTasks = (values: FormValues, municipalityData: MunicipalityData) => {
    const getError = ({ timepoint, taskName, frequency, level }: { timepoint?: string; taskName: string; frequency?: number; level?: string }, municipalityData: MunicipalityData) => {
      // Ej vald insats
      if (frequency === undefined && level === undefined) return null
      // Samma regel som uträkningen: bara ifyllda insatser räknas, övriga varnas för (#79)
      const frequencies = timepoint ? undefined : municipalityData.input.extraTasks.find((task) => task.name === taskName)?.frequencies
      if (isTaskComplete(timepoint ? 'timepoint' : 'other', level, frequency, frequencies)) return null
      // Först vad det gäller, sedan vad som saknas (#79)
      const taskString = (
        <>
          {timepoint && `${timepoint.trim()} – `}
          <b>{taskName.trim()}</b>
        </>
      )

      if (!level) {
        return {
          message: <>{taskString}. Nivå saknas.</>,
          reason: 'level',
        } as const
      }
      return {
        message: <>{taskString}. Frekvens saknas.</>,
        reason: 'frequency',
      } as const
    }

    const timepointEntries = values ? (Object.entries(values).filter(([key, value]) => ['shared', 'save', 'WeeksOff', 'other'].indexOf(key) === -1) as [string, any][]) : []

    const timepointTaskEntries = timepointEntries.flatMap(([timepoint, tasks]) => {
      return tasks.timepoints
        ? Object.entries(tasks.timepoints).map(([taskName, task]) => {
            return { timepoint, taskName, frequency: (task as any)?.Besök, level: (task as any)?.Nivå }
          })
        : []
    })

    const otherEntries = values.other
      ? (Object.entries(values.other) as [string, any][]).map(([taskName, task]) => {
          return { taskName, frequency: (task as any)?.Frekvens, level: (task as any)?.Nivå }
        })
      : []

    const errors: SubmitError[] = [...timepointTaskEntries, ...otherEntries].map((entry) => getError(entry, municipalityData)).filter((error) => error !== null) as SubmitError[]

    // Check max allocations per task
    for (const task of municipalityData.input.timepointTasks) {
      if (task.maxAllocations == null) continue
      const allocatedTimepoints = timepointEntries.filter(([, tasks]) => {
        const taskData = tasks.timepoints?.[task.name]
        return taskData && (taskData.Besök != null || taskData.Nivå != null)
      })
      if (allocatedTimepoints.length > task.maxAllocations) {
        errors.push({
          message: (
            <>
              <b>{task.name.trim()}</b>. Insatsen är vald på {allocatedTimepoints.length} tidpunkter men kan bara väljas på {task.maxAllocations === 1 ? 'en' : task.maxAllocations}.
            </>
          ),
          reason: 'maxAllocations',
        })
      }
    }

    // Check tasks that must be alone on their timepoint
    for (const [timepoint, tasks] of timepointEntries) {
      const allocatedTasks = municipalityData.input.timepointTasks.filter((task) => {
        const taskData = tasks.timepoints?.[task.name]
        return taskData && (taskData.Besök != null || taskData.Nivå != null)
      })
      const exclusiveTask = allocatedTasks.find((task) => task.exclusive)
      if (exclusiveTask && allocatedTasks.length > 1) {
        errors.push({
          message: (
            <>
              {timepoint.trim()} – <b>{exclusiveTask.name.trim()}</b>. Insatsen kan inte kombineras med andra insatser på samma tidpunkt.
            </>
          ),
          reason: 'exclusive',
        })
      }
    }

    return errors
  }

  interface FormValues {
    shared?: undefined | boolean
    WeeksOff?: undefined | number
    // Should not exist in the form values
    save: undefined
    other: Record<string, { Frekvens: number; Nivå: number }>
    // Timepoints
    [key: string]:
      | { timepoints: Record<string, { Besök: number; Nivå: number }> }
      // To satisfy ts
      | boolean
      | number
      | undefined
      | Record<string, { Frekvens: number; Nivå: number }>
  }

  const onSave = async () => {
    if (!municipalityData) return
    try {
      const values = form.getFieldsValue()
      const formatedValues = jsonFormatter(values, weeksOff, municipalityData)

      if (!pid || !pnr) {
        notificationInstance.error({
          message: `Skriv in personnummer och ${municipalityData?.input.formSettings.nameProcessId ?? 'ID'} för att spara`,
        })
        return null
      }
      const validatedPersonnummer = validatePersonnummer(pnr)
      if (validatedPersonnummer === null) {
        notificationInstance.error({
          message: 'Personnumret är inte giltigt',
        })
        return null
      }
      // Kryssrutan "Spara som utkast" ligger under save i formuläret (#69)
      const draft = values.save?.Draft === true
      await saveBeslutsstod(api, formatedValues, pid, validatedPersonnummer, draft, note)
      setSavedStatus({ pnr: validatedPersonnummer, time: new Date().toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) })
      notificationInstance.success({
        message: 'Beräkningen har sparats',
      })
    } catch (error) {
      notificationInstance.error({
        message: 'Beräkningen kunde inte sparas',
      })
    }
  }

  const onPdfClick = async () => {
    if (!municipalityData) return
    const values = form.getFieldsValue()
    const formatedValues = jsonFormatter(values, weeksOff, municipalityData)
    try {
      const pdfData = await downloadPdf(formatedValues)
      const blob = new Blob([pdfData as any], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'beslutsstöd.pdf'
      a.click()
    } catch (error) {
      notification.error({
        message: 'PDF:en kunde inte laddas ned',
      })
    }
  }

  const onFinish = async (values: FormValues) => {
    if (!municipalityData || calculating) return
    setSubmittingErrors(null)
    const erroneousTasks = getErroneousTasks(values, municipalityData)
    const incomplete = erroneousTasks.length > 0 ? erroneousTasks : null

    console.log('Received values of form: ', JSON.stringify(values, null, 2))

    const formatedValues = jsonFormatter(values, weeksOff, municipalityData)
    setFormattedInput(formatedValues)
    setCalculating(true)
    try {
      const response = await calculateBeslutsstod(api, formatedValues)
      setResult(response)
      // Ofullständig inmatning visas tillsammans med resultatet, inte före (#79)
      setSubmittingErrors(incomplete)
      setScrollToResult(true)
    } catch (error) {
      setSubmittingErrors(incomplete)
      notificationInstance.error({
        message: 'Beräkningen kunde inte genomföras',
      })
    } finally {
      setCalculating(false)
    }
  }

  const changeFromButtonClick = () => {
    setEditMode(!editMode)
  }

  const saveEdit = async () => {
    console.log(municipalityData)
    try {
      await editBeslutsstod(api, municipalityData, editDescription)
      notificationInstance.success({
        message: 'Ändringarna har sparats',
      })
    } catch (error) {
      notificationInstance.error({
        message: 'Ändringarna kunde inte sparas',
      })
    }
  }

  const onPnrChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPnr(event.target.value)
  }
  const onPidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPid(event.target.value)
  }
  const onNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value)
  }

  useEffect(() => {
    const fetchData = async () => {
      const { config, descriptions } = await getMunicipalityData(api)
      let editableData = JSON.parse(JSON.stringify(config))
      setDescriptions(descriptions)
      dispatch(setMunicipality({ municipalityData: editableData }))
      if (id !== undefined) {
        try {
          const response = await loadBeslutsstodById(api, id)

          const inputState = JSON.parse(response.input_state)

          const searchParams = new URLSearchParams(location.search)
          const includeId = searchParams.get('includeId') !== 'false'
          const includeComment = searchParams.get('includeComment') !== 'false'

          if (includeComment && response.note) setNote(response.note)
          if (response.ssn) {
            setPnr(response.ssn)
            setLoadedStatus({ pnr: response.ssn, time: new Date().toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) })
          }
          if (includeId && response.process_id) setPid(response.process_id)
          const { result: converted, errors: loaderErrors } = jsonLoader(inputState.state, config)
          const errors = [...loaderErrors, ...getDescriptionChangeErrors(response.descriptionChanges ?? [], converted)]
          const checkedTimepoints = Object.keys(inputState.state.timepointTasks)
            .filter((key) => converted[key])
            .sort((a, b) => config.input.timepoints.indexOf(a) - config.input.timepoints.indexOf(b))
          setCheckedTimepoints(checkedTimepoints)

          const loadedWeeksOff = typeof inputState.state.weeksOff === 'number' && inputState.state.weeksOff > 0 ? inputState.state.weeksOff : 0
          setWeeksOff(loadedWeeksOff)

          form.setFieldsValue({
            ...converted,
            save: { Personnummer: response.ssn ?? '', ID: includeId ? response.process_id ?? '' : '', Note: includeComment ? response.note ?? '' : '' },
          })

          const filteredState = { ...inputState.state }

          // Sammanställningen visar bara ifyllda insatser, med samma regel som uträkningen (#79)
          const savedFrequency = (task: any) => Number(task.frequency) || undefined
          if (filteredState.extraTasks) {
            filteredState.extraTasks = Object.fromEntries(
              Object.entries(filteredState.extraTasks).filter(([name, task]: [string, any]) =>
                isTaskComplete('other', task.selection, savedFrequency(task), config.input.extraTasks.find((extraTask: any) => extraTask.name === name)?.frequencies)
              )
            )
          }
          if (filteredState.timepointTasks) {
            filteredState.timepointTasks = Object.fromEntries(
              Object.entries(filteredState.timepointTasks).map(([timepoint, tasks]: [string, any]) => [
                timepoint,
                Object.fromEntries(Object.entries(tasks).filter(([, task]: [string, any]) => isTaskComplete('timepoint', task.selection, savedFrequency(task)))),
              ])
            )
          }

          if (errors.length > 0) {
            // Inläsningen tog bort eller ändrade något, så det sparade resultatet stämmer inte med
            // formuläret. Resultat, Spara och sammanställning visas först efter Beräkna (#79).
            setLoadingErrors(errors)
            setMarkedLoadingErrors(errors)
          } else {
            setFormattedInput(filteredState)
            setLoadedResult(inputState.calculatedTime)
          }
          // Snurran tas bort och formuläret ritas upp innan notisen visas, så att notisen och varningarna
          // kommer samtidigt. Notisen kräver dessutom att sidan just ritats om – annars kastar antd
          // "getPrefixCls is not a function" (#79).
          setLoadingCopy(false)
          if (errors.length > 0) {
            notificationInstance.warning({
              message: 'Beräkningen har kopierats med varningar',
            })
          } else {
            notificationInstance.success({
              message: 'Beräkningen har kopierats',
            })
          }
        } catch {
          setLoadingCopy(false)
          notificationInstance.error({
            message: 'Beräkningen kunde inte kopieras',
          })
        }
      }
    }
    fetchData()
  }, [])
  const renderSwitch = (tab: string) => {
    if (!me) return null
    switch (tab) {
      case '1':
        return (
          <>
            {loadingCopy && (
              <div className="flex justify-center pt-20">
                <Spin />
              </div>
            )}
            {/* Formuläret ligger kvar (dolt) medan beräkningen läses in, så att det kan fyllas i */}
            <div style={{ display: loadingCopy ? 'none' : undefined }}>
              {loadingErrors && loadingErrors.length > 0 && (
                <div className="mb-4">
                  <Alert
                    message="Beräkningen har kopierats med varningar"
                    description={
                      <>
                        <ul>
                          {loadingErrors.map((error, index) => (
                            <li key={index}>- {error.message}</li>
                          ))}
                        </ul>
                        <p>Kontrollera varningarna ovan och tryck sedan Beräkna för att se Sammanställd tid och för att kunna spara beräkningen.</p>
                      </>
                    }
                    type="warning"
                    closable
                    showIcon
                  />
                </div>
              )}
              <div className="mb-6 flex items-center gap-1">
                <Select
                  mode="multiple"
                  allowClear
                  className="beslutsstod-timepoint-picker"
                  style={{ minWidth: '225px' }}
                  listHeight={400}
                  placeholder="Välj tidpunkter"
                  value={checkedTimepoints}
                  onChange={onChange}
                  options={timepointOptions(municipalityData?.input.timepoints ?? [])}
                />
                {editMode && municipalityData && <EditableTimepoints />}
                {me.permissions.includes('edit_beslutsstöd') && (
                  <Switch
                    checkedChildren={<SettingOutlined />}
                    unCheckedChildren={<CalculatorOutlined />}
                    checked={editMode}
                    onChange={changeFromButtonClick}
                    title={editMode ? 'Ändra till beräkna' : 'Ändra till redigera'}
                  />
                )}
                <Popover overlayClassName="decision-popover" title="Ändringshistorik" content={<TimelineTooltip descriptions={descriptions} />}>
                  <InfoCircleOutlined />
                </Popover>
              </div>
              <Form
                form={form}
                name={'dynamic-form'}
                onFinish={onFinish}
                onValuesChange={(changedValues) => {
                  setSavedStatus(null)
                  setMarkedLoadingErrors((current) => current && withoutErrorsForChangedFields(current, changedValues))
                  const keys = Object.keys(changedValues || {})
                  const onlySaveChanged = keys.length > 0 && keys.every((k) => k === 'save')
                  if (!onlySaveChanged) {
                    setResult(null)
                    setLoadedResult(null)
                    setFormattedInput(null)
                    setSubmittingErrors(null)
                  }
                }}
              >
                {!editMode && (
                  <>
                    <TimepointsTasks timepoints={checkedTimepoints} loadingErrors={markedLoadingErrors ?? undefined} />
                    <Other loadingErrors={markedLoadingErrors ?? undefined} />
                  </>
                )}
                {editMode && municipalityData && (
                  <>
                    <EditTimepointsTasks municipalityData={municipalityData} onChange={(municipalityData) => dispatch(setMunicipality({ municipalityData }))} />
                    <EditableOther municipalityData={municipalityData} onChange={(municipalityData) => dispatch(setMunicipality({ municipalityData }))} />
                    <Typography.Title
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBlock: '40px',
                      }}
                      level={3}
                    >
                      Övriga Inställningar
                    </Typography.Title>
                    <FormSettings />
                    <EditableCalculationSettings />
                    <TextArea
                      value={editDescription}
                      style={{ width: 500 }}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Förklara dina ändringar"
                      autoSize={{ minRows: 3, maxRows: 5 }}
                    />
                    <Button onClick={saveEdit} type="primary" style={{ marginLeft: '4px' }}>
                      Spara ändringar
                    </Button>
                    <Divider />
                  </>
                )}
                {!editMode &&
                  (() => {
                    const hasResult = Boolean(result || loadedResult)
                    const showGrid = hasResult || Boolean(submittingErrors)
                    return (
                      <>
                        <div className="mt-12 flex flex-wrap items-center gap-4">
                          <Form.Item noStyle>
                            <Button type="primary" htmlType="submit" className="w-[150px]" style={{ pointerEvents: calculating ? 'none' : undefined }}>
                              <span>Beräkna</span>
                              {calculating && <LoadingOutlined />}
                            </Button>
                          </Form.Item>
                          {municipalityData && municipalityData.input.formSettings.showShared !== false && (
                            <Form.Item name={['shared']} valuePropName="checked" noStyle>
                              <Checkbox>
                                {municipalityData.input.formSettings.nameShared}{' '}
                                <Popover overlayClassName="decision-popover" title={municipalityData.input.formSettings.nameShared} content={municipalityData.input.formSettings.descriptionShared}>
                                  <InfoCircleOutlined />
                                </Popover>
                              </Checkbox>
                            </Form.Item>
                          )}
                          {municipalityData && municipalityData.input.formSettings.showWeeksOff !== false && (
                            <Form.Item name={['WeeksOff']} noStyle>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <InputNumber type="number" value={weeksOff} onChange={(value) => setWeeksOff(value ?? 0)} addonAfter="veckor" min={0} max={4} style={{ maxWidth: '150px' }} />
                                {municipalityData.input.formSettings.nameWeeksOff}
                                <Popover overlayClassName="decision-popover" title={municipalityData.input.formSettings.nameWeeksOff} content={municipalityData.input.formSettings.descriptionWeeksOff}>
                                  <InfoCircleOutlined />
                                </Popover>
                              </div>
                            </Form.Item>
                          )}
                          {!me.permissions.includes('save_beslutsstöd') && (
                            <Button type="primary" htmlType="button" className="w-[150px]" onClick={onPdfClick} loading={isPdfLoading}>
                              Ladda ned pdf
                            </Button>
                          )}
                        </div>

                        <Divider className="my-6" />

                        {showGrid && (
                          <div ref={resultRef} className="grid scroll-mt-16 grid-cols-1 gap-8 md:grid-cols-2">
                            <div className="flex flex-col gap-6 md:border-r md:border-[#f0f0f0] md:pr-8">
                              {submittingErrors && (
                                <Alert
                                  className="w-fit max-w-full"
                                  message="Ofullständig inmatning"
                                  description={
                                    <ul>
                                      {submittingErrors.map((error, index) => (
                                        <li key={index}>- {error.message}</li>
                                      ))}
                                    </ul>
                                  }
                                  type="warning"
                                  closable
                                  showIcon
                                />
                              )}
                              {hasResult && municipalityData?.input.calculationOutputSettings.unit && (
                                <ShowResults result={result ?? loadedResult} unit={municipalityData.input.calculationOutputSettings.unit} compact />
                              )}
                            </div>
                            <div>
                              {hasResult && me.permissions.includes('save_beslutsstöd') && (
                                <>
                                  <Typography.Title level={3} className="mb-2 text-left">
                                    Spara
                                  </Typography.Title>
                                  <Form.List name={'save'}>
                                    {() => (
                                      <div className="flex w-full max-w-[520px] flex-col gap-4">
                                        <div className="flex gap-2">
                                          <Form.Item name={['Personnummer']} noStyle>
                                            <Input onChange={onPnrChange} placeholder="Personnummer" />
                                          </Form.Item>
                                          <Form.Item name={['ID']} noStyle>
                                            <Input onChange={onPidChange} placeholder={municipalityData?.input.formSettings.nameProcessId ?? 'ID'} />
                                          </Form.Item>
                                        </div>
                                        <Form.Item name={['Note']} noStyle>
                                          <Input.TextArea onChange={onNoteChange} placeholder="Kommentar" rows={4} style={{ resize: 'none' }} />
                                        </Form.Item>
                                        <div className="flex items-center gap-4">
                                          <Button type="primary" htmlType="button" className="w-[150px]" onClick={onSave}>
                                            Spara
                                          </Button>
                                          <Form.Item name={['Draft']} valuePropName="checked" noStyle>
                                            <Checkbox>Spara som utkast</Checkbox>
                                          </Form.Item>
                                        </div>
                                      </div>
                                    )}
                                  </Form.List>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {hasResult && formattedInput && municipalityData?.input.calculationOutputSettings.showTaskDistribution && (
                          <div className="mt-8">
                            <TaskDistributionTable
                              timepointTasks={formattedInput.timepointTasks}
                              extraTasks={formattedInput.extraTasks}
                              timepointOrder={municipalityData.input.timepoints}
                              taskOrder={municipalityData.input.timepointTasks.map((t) => t.name)}
                              extraTaskOrder={municipalityData.input.extraTasks.map((t) => t.name)}
                            />
                          </div>
                        )}
                      </>
                    )
                  })()}
              </Form>
            </div>
          </>
        )

      case '2':
        return <SearchBeslutsstod />

      default:
        return null
    }
  }

  return (
    <>
      {contextHolder}
      {tab !== '2' && (savedStatus || loadedStatus) && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: '#e6f7ff',
            border: '1px solid #91d5ff',
            marginTop: -24,
            marginLeft: -24,
            marginRight: -24,
            marginBottom: 12,
            width: 'calc(100% + 48px)',
          }}
        >
          <span style={{ fontWeight: 600 }}>{(savedStatus ?? loadedStatus)?.pnr}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#595959' }}>
            {savedStatus && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Sparad {savedStatus.time}</span>}
            {savedStatus && loadedStatus && <span style={{ color: '#d9d9d9' }}>·</span>}
            {loadedStatus && <span>Hämtad {loadedStatus.time}</span>}
          </div>
        </div>
      )}
      <FlagListHeader title="" heading="Beräkningsstöd för tid i hemtjänsten" description={null} button={false} />

      <Tabs onTabClick={(key: string) => setTab(key)}>
        <TabPane tab={'Beräkna'} key={'1'} />
        {me?.permissions.includes('save_beslutsstöd') && <TabPane tab={'Sök'} key={'2'} />}
      </Tabs>

      {renderSwitch(tab)}
    </>
  )
}

export default Beslutsstod
